import React from 'react';
import PropTypes from 'prop-types';

import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

const ALL_TYPES = 'ALL';

const statusColumns = ['open', 'pending'];
const statusColumnsWithComplete = ['open', 'pending', 'complete'];
const stalledColumns = ['OVERDUE_JOB', 'STUCK_OPEN', 'NOTHING_SCHEDULED'];
const markColumns = ['MARKED_BAD', 'FLAGGED', 'HUMAN_INTERVENTION'];

const columnTitleKeys = {
  open: 'OPEN',
  pending: 'PENDING',
  complete: 'COMPLETE',
};

const columnColours = {
  OVERDUE_JOB: '#B77900',
  STUCK_OPEN: '#B77900',
  NOTHING_SCHEDULED: '#B77900',
  MARKED_BAD: '#C4512C',
};

const headerCellSx = {
  fontSize: '11px',
  fontWeight: 500,
  color: '#A8A49E',
  borderBottom: '1px solid #ECECF1',
  whiteSpace: 'nowrap',
  py: 1,
};

const groupCellSx = {
  ...headerCellSx,
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  borderBottom: 'none',
  textAlign: 'right',
  pb: 0,
};

const numberCellSx = {
  fontSize: '12px',
  fontFamily: 'monospace',
  textAlign: 'right',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #F1F1F4',
  py: 1.25,
};

const groupStartSx = {
  borderLeft: '1px solid #ECECF1',
};

const EmptyValue = (props) => {
  return (
    <Box component='span' sx={{color: '#D0CDC8'}}>
      –
    </Box>
  );
};

const CountValue = (props) => {
  const {
    columnKey,
    value,
  } = props;

  if (!value) {
    return <EmptyValue />;
  }

  return (
    <Box component='span' sx={{color: columnColours[columnKey] || '#1A1916', fontWeight: columnColours[columnKey] ? 600 : 400}}>
      {value}
    </Box>
  );
};

CountValue.propTypes = {
  columnKey: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
};

// "0 of 12" stays visible: a dash would hide how many cases were marked.
const MarkedBadValue = (props) => {
  const {
    markedBad,
    marked,
  } = props;

  if (!marked) {
    return <EmptyValue />;
  }

  return (
    <>
      <Box component='span' sx={{color: markedBad ? '#C4512C' : '#A8A49E', fontWeight: markedBad ? 600 : 400}}>
        {markedBad}
      </Box>
      <Box component='span' sx={{color: '#A8A49E'}}>
        {` ${translate('case.Monitor.Table.OfTotal')} ${marked}`}
      </Box>
    </>
  );
};

MarkedBadValue.propTypes = {
  markedBad: PropTypes.number.isRequired,
  marked: PropTypes.number.isRequired,
};

const SummaryRow = (props) => {
  const {
    summary,
    statusColumnKeys,
    markColumnKeys,
    isSelected,
    isTotal,
    marksLoaded,
    onSelect,
  } = props;

  return (
    <TableRow
      hover
      onClick={onSelect}
      sx={{
        'cursor': 'pointer',
        'backgroundColor': isSelected ? '#FFF4EF' : (isTotal ? '#F9F8F6' : 'transparent'),
        'boxShadow': isSelected ? 'inset 3px 0 0 #D9673E' : 'none',
        '&.MuiTableRow-hover:hover': {
          backgroundColor: isSelected ? '#FFF4EF' : '#FAFAFB',
        },
      }}
    >
      <TableCell
        sx={{
          fontSize: isTotal ? '13px' : '12px',
          fontWeight: isTotal ? 600 : 500,
          fontFamily: isTotal ? 'inherit' : 'monospace',
          color: isSelected ? '#C4512C' : '#1A1916',
          borderBottom: '1px solid #F1F1F4',
          whiteSpace: 'nowrap',
          py: 1.25,
        }}
      >
        {isTotal ? translate('case.Monitor.Table.AllTypes') : summary.caseType}
      </TableCell>
      {
        statusColumnKeys.map((columnKey, index) => (
          <TableCell
            key={columnKey}
            sx={{...numberCellSx, ...(index === 0 ? groupStartSx : {}), fontWeight: isTotal ? 600 : 400}}
          >
            <CountValue columnKey={columnKey} value={summary[columnKey]} />
          </TableCell>
        ))
      }
      {
        stalledColumns.map((columnKey, index) => (
          <TableCell
            key={columnKey}
            sx={{...numberCellSx, ...(index === 0 ? groupStartSx : {})}}
          >
            <CountValue columnKey={columnKey} value={summary[columnKey]} />
          </TableCell>
        ))
      }
      {
        markColumnKeys.map((columnKey, index) => (
          <TableCell
            key={columnKey}
            sx={{...numberCellSx, ...(index === 0 ? groupStartSx : {})}}
          >
            {
              !marksLoaded ?
                <EmptyValue /> :
                columnKey === 'MARKED_BAD' ?
                  <MarkedBadValue markedBad={summary.MARKED_BAD} marked={summary.marked} /> :
                  <CountValue columnKey={columnKey} value={summary[columnKey]} />
            }
          </TableCell>
        ))
      }
    </TableRow>
  );
};

SummaryRow.propTypes = {
  summary: PropTypes.object.isRequired,
  statusColumnKeys: PropTypes.array.isRequired,
  markColumnKeys: PropTypes.array.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isTotal: PropTypes.bool,
  marksLoaded: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const TypeTable = (props) => {
  const {
    typeSummaries,
    total,
    selectedCaseType,
    showMarks,
    marksLoaded,
    marksWindowLabel,
    showComplete,
    onSelectCaseType,
  } = props;

  // Complete only has a count once the completed cases are loaded.
  const statusColumnKeys = showComplete ? statusColumnsWithComplete : statusColumns;
  const markColumnKeys = showMarks ? markColumns : [];
  const columnCount = 1 + statusColumnKeys.length + stalledColumns.length + markColumnKeys.length;

  return (
    <TableContainer sx={{overflowX: 'auto'}}>
      <Table size='small' sx={{minWidth: 960}}>
        <TableHead>
          <TableRow>
            <TableCell sx={{...groupCellSx, width: '100%'}} />
            <TableCell colSpan={statusColumnKeys.length} sx={groupCellSx}>
              <Translate expression='case.Monitor.Table.Groups.Status' />
            </TableCell>
            <TableCell colSpan={stalledColumns.length} sx={groupCellSx}>
              <Translate expression='case.Monitor.Table.Groups.Stalled' />
            </TableCell>
            {
              showMarks &&
              <TableCell colSpan={markColumnKeys.length} sx={groupCellSx}>
                {translate('case.Monitor.Table.Groups.Marks', {window: marksWindowLabel})}
              </TableCell>
            }
          </TableRow>
          <TableRow>
            <TableCell sx={headerCellSx}>
              <Translate expression='case.Monitor.Table.Type' />
            </TableCell>
            {
              [...statusColumnKeys, ...stalledColumns, ...markColumnKeys].map((columnKey) => (
                <TableCell
                  key={columnKey}
                  sx={{
                    ...headerCellSx,
                    textAlign: 'right',
                    ...([statusColumns[0], stalledColumns[0], markColumns[0]].includes(columnKey) ? groupStartSx : {}),
                  }}
                >
                  <Translate expression={`case.Monitor.Table.Columns.${columnTitleKeys[columnKey] || columnKey}`} />
                </TableCell>
              ))
            }
          </TableRow>
        </TableHead>
        <TableBody>
          {
            typeSummaries.map((summary) => (
              <SummaryRow
                key={summary.caseType}
                summary={summary}
                statusColumnKeys={statusColumnKeys}
                markColumnKeys={markColumnKeys}
                isSelected={selectedCaseType === summary.caseType}
                marksLoaded={marksLoaded}
                onSelect={() => onSelectCaseType(summary.caseType)}
              />
            ))
          }
          {
            typeSummaries.length === 0 &&
            <TableRow>
              <TableCell colSpan={columnCount} sx={{borderBottom: '1px solid #F1F1F4', py: 2}}>
                <Typography sx={{fontSize: '13px', color: '#A8A49E'}}>
                  <Translate expression='case.Monitor.Table.Empty' />
                </Typography>
              </TableCell>
            </TableRow>
          }
          <SummaryRow
            summary={total}
            statusColumnKeys={statusColumnKeys}
            markColumnKeys={markColumnKeys}
            isSelected={selectedCaseType === ALL_TYPES}
            isTotal
            marksLoaded={marksLoaded}
            onSelect={() => onSelectCaseType(ALL_TYPES)}
          />
        </TableBody>
      </Table>
    </TableContainer>
  );
};

TypeTable.propTypes = {
  typeSummaries: PropTypes.array.isRequired,
  total: PropTypes.object.isRequired,
  selectedCaseType: PropTypes.string.isRequired,
  showMarks: PropTypes.bool.isRequired,
  marksLoaded: PropTypes.bool.isRequired,
  marksWindowLabel: PropTypes.string.isRequired,
  showComplete: PropTypes.bool.isRequired,
  onSelectCaseType: PropTypes.func.isRequired,
};

export default React.memo(TypeTable);
